/**
 *
 * @module bootstrapConfiguration
 *
 */

import Check from 'check-types';
import ConfigurationHostName from './configurationHostName';
import Platform from './platform';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    includes,
    boolean,
    nonEmptyString,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class BootstrapConfiguration
 * @desc Provides configuration information used by the ConfigurationClient to
 * retrieve the SdkSessionConfiguration necessary to create a SdkSession instance.
 *
 */
export default class BootstrapConfiguration {

    /**
     *
     * @param {Object} options
     * @param {String} options.clientId
     * @param {String} options.clientApiKey
     * @param {String} options.environment
     * @param {Boolean} [options.debugEnabled=false]
     * @param {String} [options.platform=null]
     * @param {ConfigurationHostName} [options.configHostName=ConfigurationHostName.prod]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'BootstrapConfiguration(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get clientId() {
                return `${this.ctor} options.clientId should be a String with length`;
            },
            get clientApiKey() {
                return `${this.ctor} options.clientApiKey should be a String with length`;
            },
            get environment() {
                return `${this.ctor} options.environment should be a String with length`;
            },
            get debugEnabled() {
                return `${this.ctor} options.debugEnabled should be a Boolean`;
            },
            get platform() {
                return `${this.ctor} options.platform should be a String with length`;
            },
            get configHostName() {
                return `${this.ctor} options.configHostName should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { clientId, clientApiKey, environment, debugEnabled = false } = options;
        const { platform = null, configHostName = ConfigurationHostName.prod } = options;

        nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        nonEmptyString(clientApiKey, errorMessage.clientApiKey, InvalidArgumentException);
        nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);
        boolean(debugEnabled, errorMessage.debugEnabled, InvalidArgumentException);

        if (Check.assigned(platform)) {

            includes(
                Platform,
                platform,
                errorMessage.platform,
                InvalidArgumentException
            );

        }

        includes(
            ConfigurationHostName,
            configHostName,
            errorMessage.configHostName,
            InvalidArgumentException
        );

        /**
         *
         * @access public
         * @type {String}
         * @desc The client ID to use the SDK against.
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access public
         * @type {String}
         * @desc The public application key for the SDK.
         *
         */
        this.clientApiKey = clientApiKey;

        /**
         *
         * @access public
         * @type {String}
         * @desc The environment that the SDK services should run in.
         *
         */
        this.environment = environment;

        /**
         *
         * @access public
         * @type {String}
         * @desc A value that indicates whether or not to enable debug mode for the SDK.
         *
         */
        this.debugEnabled = debugEnabled;

        /**
         *
         * @access public
         * @type {String}
         * @desc The application runtime used for device.
         *
         */
        this.platform = platform;

        /**
         *
         * @access public
         * @type {String}
         * @desc The configuration host name to use ("dev" or "prod").
         *
         */
        this.configHostName = configHostName;

    }

}



// WEBPACK FOOTER //
// ./src/services/configuration/bootstrapConfiguration.js